import React from 'react';
import { Link } from 'react-router-dom';

import { Avatar, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

import { VennueAccessRequestItem } from '../../../graphql/API';
import useVenueId from '../../../hooks/useVenueId';
import Icons from '../../../themes/icons';
import Button from '../../Button/Button';
import BaseDialog, { BaseDialogProps } from '../Base';

export const accessRequestPopupStorageKey = 'dont-access-request-success-popup';

type Props = {
  accessRequest: VennueAccessRequestItem | null;
  reservationID?: string | null;
} & BaseDialogProps;

export const AccessRequestAcceptedDialog: React.FC<Props> = ({
  accessRequest,
  reservationID,
  ...rest
}) => {
  const { user, offer } = accessRequest || {};
  const venueID = useVenueId();

  const [dontShowLocal, setDontShowLocal] = React.useState(false);

  const handleOnDontShowChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDontShowLocal(e.target.checked);
  }, []);

  React.useEffect(() => {
    localStorage.setItem(accessRequestPopupStorageKey, JSON.stringify(dontShowLocal));
    localStorage.setItem('lastLoggedInVenueID', JSON.stringify(venueID));
  }, [dontShowLocal]);

  return (
    <BaseDialog
      width='465px'
      minWidth='465'
      height={offer?.shopifyProductID ? '700px' : '650px'}
      minHeight='650px'
      {...rest}
    >
      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'space-between'}
        px={2}
        py={2}
        gap={5}
      >
        <Box>
          <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <Box position={'relative'}>
              <Avatar
                variant='rounded'
                src={String(user?.picture?.medium)}
                alt={String(user?.firstname)}
                sx={{
                  width: '230px',
                  height: '230px',
                  borderRadius: '50%',
                  objectFit: 'contain',
                }}
              />
              <Box
                position={'absolute'}
                bottom={-30}
                left={'50%'}
                sx={{ transform: 'translateX(-50%)' }}
              >
                <Icons.ReservationIcon />
              </Box>
            </Box>
          </Box>
          <Box mt={7} display={'flex'} flexDirection={'column'} gap={'12px'}>
            <Typography fontFamily={'Poppins'} fontSize={'20px'} fontWeight={500}>
              {user?.firstname}’s request has been approved!
            </Typography>
            <Typography fontFamily={'Poppins'} fontSize={'15px'}>
              You can now see her shipping address and prepare to send the {offer?.title}.{' '}
              {offer?.shopifyProductID
                ? 'You can also track and manage this request directly from your Shopify Orders page'
                : ''}
            </Typography>
          </Box>
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowLocal} // Check if selected
                onChange={handleOnDontShowChange}
                sx={{ color: '#32343A' }}
              />
            }
            label={`Don't show this again`}
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: '15px',
              },
            }}
          />
          <Button
            component={Link}
            variant='contained'
            color='primary'
            fullWidth
            sx={{ py: 1 }}
            onClick={rest?.onClose}
            to={`/reservations/${reservationID}`}
          >
            Go to reservation
          </Button>
          <Button
            variant='outlined'
            color='primary'
            fullWidth
            sx={{ py: 1 }}
            onClick={rest?.onClose}
          >
            Close
          </Button>
        </Box>
      </Box>
    </BaseDialog>
  );
};
